/**
 * Phase 7 chunk 3B — pure math + locked constants for the sky-view
 * pass-arc layer. Cesium-free so Vitest can sanity-check the locked
 * values + the sub-sample timestamp generator.
 */

/** Total number of sub-samples per pass arc (inclusive of both endpoints).
 *  ~30 samples across a typical 5-10 min pass = 10-20s per sample, smooth
 *  enough to look like an arc at any zoom without bloating the polyline
 *  count. */
export const PASS_ARC_SUB_SAMPLES = 30;

/** Polyline width in pixels for each pass arc. Same as the chunk-2D
 *  horizon ring so the two visual elements feel like one family. */
export const PASS_ARC_WIDTH_PX = 2;

/** Cesium label font for the peak-time + name annotation. */
export const PASS_ARC_LABEL_FONT = '13px monospace';

/** Locked label-offset (in pixels) above the peak point so the label
 *  doesn't overlap the polyline itself. */
export const PASS_ARC_LABEL_PIXEL_OFFSET_Y = -10;

/**
 * Generate `count` evenly-spaced timestamps between `startMs` and
 * `endMs` (inclusive of both endpoints). Returns the list as Date
 * objects ready to hand to satellite.js's `propagate`.
 *
 * Pure helper, Vitest-testable. Defensive against invalid inputs:
 *  - `count < 2` → returns `[start, end]` (two samples minimum so
 *    the polyline has both endpoints).
 *  - `endMs < startMs` → empty list (caller should validate first).
 *  - non-finite inputs → empty list.
 */
export function passArcTimestamps(startMs: number, endMs: number, count = PASS_ARC_SUB_SAMPLES): Date[] {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) return [];
  const n = Math.max(2, Math.floor(count));
  const out: Date[] = [];
  for (let i = 0; i < n; i++) {
    const t = startMs + ((endMs - startMs) * i) / (n - 1);
    out.push(new Date(t));
  }
  return out;
}

/**
 * Format a peak time into a short HH:MM label for the pass-arc tag.
 * Uses the locale's 24h convention; falls back to "—" on invalid input.
 */
export function formatPeakLabel(peakAt: string, name: string): string {
  const ms = Date.parse(peakAt);
  if (Number.isNaN(ms)) return name;
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${name} · ${hh}:${mm}`;
}
